import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Building2,
  Wallet,
  FileCheck,
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertTriangle,
  X,
  Plus,
  Sparkles,
  Shield,
  Zap
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const personalSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(5, "Phone is required"),
  dateOfBirth: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  country: z.string().min(2, "Country is required"),
  postalCode: z.string().optional()
});

const steps = [
  { id: 1, title: "Personal", subtitle: "Basic information", icon: User },
  { id: 2, title: "Business", subtitle: "Company details", icon: Building2 },
  { id: 3, title: "Crypto", subtitle: "Wallet addresses", icon: Wallet },
  { id: 4, title: "Review", subtitle: "Confirm & submit", icon: FileCheck }
];

const countries = [
  "United States", "United Kingdom", "Germany", "France", "Spain", "Italy",
  "China", "Japan", "Singapore", "Hong Kong", "UAE", "Saudi Arabia",
  "Russia", "Brazil", "Mexico", "Canada", "Australia", "India",
  "Iran", "North Korea", "Syria", "Venezuela", "Turkey", "Nigeria",
  "South Africa", "Myanmar", "Belarus", "Ukraine", "Pakistan"
];

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center mb-10">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className="flex flex-col items-center"
          >
            <div className={`
              relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300
              ${currentStep > step.id 
                ? 'step-complete' 
                : currentStep === step.id 
                  ? 'step-active' 
                  : 'step-pending'}
            `}>
              {currentStep > step.id ? (
                <CheckCircle className="h-5 w-5 text-white" />
              ) : (
                <step.icon className="h-5 w-5 text-white" />
              )}
              {currentStep === step.id && (
                <motion.div
                  className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ opacity: 0.3, zIndex: -1 }}
                />
              )}
            </div>
            <div className="mt-3 text-center">
              <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-white' : 'text-slate-500'}`}>
                {step.title}
              </p>
              <p className="text-[10px] text-slate-500">{step.subtitle}</p>
            </div>
          </motion.div>
          {index < steps.length - 1 && (
            <div className={`w-16 sm:w-24 h-0.5 mx-2 mt-[-30px] transition-all duration-500 ${
              currentStep > step.id 
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                : 'bg-slate-700'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

function RiskResult({ result, onReset }) {
  const navigate = useNavigate();
  
  const bandConfig = {
    RED: { 
      gradient: "from-red-500/30 to-red-600/10",
      textGradient: "gradient-text-risk-red",
      glow: "shadow-[0_0_60px_-15px_rgba(239,68,68,0.5)]",
      dot: "risk-dot-red",
      label: "HIGH RISK"
    },
    AMBER: { 
      gradient: "from-amber-500/30 to-amber-600/10",
      textGradient: "gradient-text-risk-amber",
      glow: "shadow-[0_0_60px_-15px_rgba(245,158,11,0.5)]",
      dot: "risk-dot-amber",
      label: "MEDIUM RISK"
    },
    GREEN: { 
      gradient: "from-emerald-500/30 to-emerald-600/10",
      textGradient: "gradient-text-risk-green",
      glow: "shadow-[0_0_60px_-15px_rgba(16,185,129,0.5)]",
      dot: "risk-dot-green",
      label: "LOW RISK"
    }
  };

  const config = bandConfig[result.band] || bandConfig.GREEN;

  const nodes = (result.network?.nodes || []).map((node, i) => ({
    id: node.id,
    position: { x: 250 + (i % 3) * 200, y: 100 + Math.floor(i / 3) * 150 },
    data: { label: node.label },
    style: {
      background: node.risk === 'high' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(30, 41, 59, 0.8)',
      border: node.risk === 'high' ? '2px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '12px',
      padding: '12px 16px',
      color: '#f8fafc',
      fontSize: '12px',
      fontFamily: 'Inter',
      backdropFilter: 'blur(10px)'
    }
  }));

  const edges = (result.network?.edges || []).map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    labelStyle: { fill: '#94a3b8', fontSize: 10, fontFamily: 'Inter' },
    style: { stroke: 'rgba(99, 102, 241, 0.4)', strokeWidth: 2 }
  }));

  return (
    <motion.div 
      className="space-y-6" 
      data-testid="risk-result"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Risk Score Hero */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={`glass-card rounded-3xl p-10 text-center relative overflow-hidden ${config.glow}`}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-50`} />
        <div className="relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
          >
            <Sparkles className="h-4 w-4 text-blue-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">AI Risk Assessment Complete</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 150 }}
            className="flex items-center justify-center gap-4 mb-6"
          >
            <span className={`text-8xl font-bold ${config.textGradient}`} style={{ fontFamily: 'JetBrains Mono' }}>
              {result.score}
            </span>
            <span className="text-4xl text-slate-500 font-light">/100</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10"
          >
            <div className={`risk-dot ${config.dot}`} style={{ width: 14, height: 14 }} />
            <span className={`text-xl font-bold ${config.textGradient}`} data-testid="risk-band">
              {config.label}
            </span>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="mt-6 text-sm text-slate-400 max-w-lg mx-auto leading-relaxed"
          >
            {result.recommendation}
          </motion.p>
        </div>
      </motion.div>

      {/* Risk Factors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="glass-card rounded-2xl border-0 overflow-hidden">
          <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20">
              <Shield className="h-4 w-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Screening Results</h3>
              <p className="text-xs text-slate-500">Detailed risk factor analysis</p>
            </div>
          </div>
          <CardContent className="p-0 divide-y divide-white/5">
            {result.factors?.map((factor, index) => (
              <Collapsible key={index}>
                <CollapsibleTrigger className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`risk-dot ${factor.hit ? 'risk-dot-red' : 'risk-dot-green'}`} />
                    <span className="text-sm font-medium text-white">{factor.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-bold font-mono-data ${
                      factor.hit ? 'text-red-400' : 'text-slate-500'
                    }`}>
                      {factor.hit ? `+${factor.points}` : '0'} pts
                    </span>
                    {factor.hit ? (
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-emerald-400" />
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="px-6 pb-4">
                  <div className="pl-8 border-l-2 border-slate-700 ml-1">
                    <p className="text-xs text-slate-500 mb-1">Source: {factor.source}</p>
                    <p className="text-sm text-slate-400">{factor.details}</p>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* Network Graph */}
      {nodes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass-card rounded-2xl border-0 overflow-hidden">
            <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20">
                <Zap className="h-4 w-4 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Entity Network</h3>
                <p className="text-xs text-slate-500">Relationship mapping</p>
              </div>
            </div>
            <CardContent className="p-0">
              <div className="h-[350px]" data-testid="network-graph">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  fitView
                  attributionPosition="bottom-left"
                >
                  <Background color="rgba(99, 102, 241, 0.1)" gap={25} />
                  <Controls className="bg-slate-900/80 border-slate-700 rounded-lg" />
                </ReactFlow>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <Button 
          onClick={() => navigate('/cases')}
          className="flex-1 btn-premium text-white rounded-xl py-6 text-sm font-semibold"
          data-testid="view-cases-after-submit"
        >
          View Cases
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button 
          variant="outline"
          onClick={onReset}
          className="flex-1 border-white/10 hover:bg-white/5 rounded-xl py-6 text-sm font-semibold"
          data-testid="onboard-another"
        >
          Onboard Another Client
        </Button>
      </motion.div>
    </motion.div>
  );
}

export default function Onboard() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [business, setBusiness] = useState({ companyName: '', registrationNumber: '', ubos: [] });

  const { register, formState: { errors }, getValues, setValue, watch, trigger } = useForm({
    resolver: zodResolver(personalSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      street: '',
      city: '',
      country: '',
      postalCode: ''
    }
  });

  const addWallet = () => {
    setWallets([...wallets, { address: '', network: 'Ethereum' }]);
  };

  const removeWallet = (index) => {
    setWallets(wallets.filter((_, i) => i !== index));
  };

  const updateWallet = (index, field, value) => {
    const updated = [...wallets];
    updated[index][field] = value;
    setWallets(updated);
  };

  const handleNext = async () => {
    if (step === 1) {
      const isValid = await trigger(["name", "email", "phone", "country"]);
      if (!isValid) {
        toast.error("Please fill in all required fields");
        return;
      }
    }
    setStep(step + 1);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const values = getValues();
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        dateOfBirth: values.dateOfBirth,
        address: {
          street: values.street,
          city: values.city,
          country: values.country,
          postalCode: values.postalCode
        },
        business,
        wallets
      };

      const response = await axios.post(`${API}/assess-risk`, payload);
      setResult(response.data);
      toast.success("Risk assessment completed!");
    } catch (error) {
      toast.error("Assessment failed: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setResult(null);
    setWallets([]);
    setBusiness({ companyName: '', registrationNumber: '', ubos: [] });
  };

  if (result) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="onboard-page">
        <RiskResult result={result} onReset={resetForm} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="onboard-page">
      <StepIndicator currentStep={step} />

      <Card className="glass-card rounded-2xl border-0 overflow-hidden">
        <div className="px-6 py-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20">
              {(() => {
                  const StepIcon = steps[step - 1].icon;
                  return <StepIcon className="h-4 w-4 text-blue-400" />;
                })()}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{steps[step - 1].title}</h3>
                <p className="text-xs text-slate-500">{steps[step - 1].subtitle}</p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Step 1: Personal Info */}
              {step === 1 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-400 text-sm">Full Name *</Label>
                      <Input 
                        {...register("name")}
                        className="input-premium h-11 rounded-xl mt-2"
                        placeholder="John Doe"
                        data-testid="input-name"
                      />
                      {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Email *</Label>
                      <Input 
                        {...register("email")}
                        type="email"
                        className="input-premium h-11 rounded-xl mt-2"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                      {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-slate-400 text-sm">Phone *</Label>
                      <Input 
                        {...register("phone")}
                        className="input-premium h-11 rounded-xl mt-2"
                        placeholder="+1-555-123-4567"
                        data-testid="input-phone"
                      />
                      {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone.message}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Date of Birth</Label>
                      <Input 
                        {...register("dateOfBirth")}
                        type="date"
                        className="input-premium h-11 rounded-xl mt-2"
                        data-testid="input-dob"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Country *</Label>
                    <Select onValueChange={(val) => setValue("country", val)} value={watch("country")}>
                      <SelectTrigger className="input-premium h-11 rounded-xl mt-2" data-testid="select-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="glass-card border-white/10">
                        {countries.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.country && <p className="text-xs text-red-400 mt-1">{errors.country.message}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div className="sm:col-span-2">
                      <Label className="text-slate-400 text-sm">Street Address</Label>
                      <Input 
                        {...register("street")}
                        className="input-premium h-11 rounded-xl mt-2"
                        placeholder="123 Main St"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">City</Label>
                      <Input 
                        {...register("city")}
                        className="input-premium h-11 rounded-xl mt-2"
                        placeholder="New York"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Business */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <Label className="text-slate-400 text-sm">Company Name</Label>
                    <Input 
                      value={business.companyName}
                      onChange={(e) => setBusiness({...business, companyName: e.target.value})}
                      className="input-premium h-11 rounded-xl mt-2"
                      placeholder="Acme Corporation"
                      data-testid="input-company"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Registration Number</Label>
                    <Input 
                      value={business.registrationNumber}
                      onChange={(e) => setBusiness({...business, registrationNumber: e.target.value})}
                      className="input-premium h-11 rounded-xl mt-2"
                      placeholder="REG-123456"
                      data-testid="input-registration"
                    />
                  </div>
                  <p className="text-xs text-slate-500 p-3 rounded-lg bg-white/5">
                    Leave blank if this is an individual client without business affiliation.
                  </p>
                </div>
              )}

              {/* Step 3: Crypto Wallets */}
              {step === 3 && (
                <div className="space-y-4">
                  <AnimatePresence>
                    {wallets.map((wallet, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex gap-3 items-start"
                      >
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <Select 
                            value={wallet.network}
                            onValueChange={(val) => updateWallet(index, 'network', val)}
                          >
                            <SelectTrigger className="input-premium h-11 rounded-xl">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="glass-card border-white/10">
                              <SelectItem value="Ethereum">Ethereum</SelectItem>
                              <SelectItem value="Bitcoin">Bitcoin</SelectItem>
                              <SelectItem value="Polygon">Polygon</SelectItem>
                              <SelectItem value="Solana">Solana</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input 
                            value={wallet.address}
                            onChange={(e) => updateWallet(index, 'address', e.target.value)}
                            className="input-premium h-11 rounded-xl sm:col-span-2 font-mono-data text-sm"
                            placeholder="0x..."
                            data-testid={`input-wallet-${index}`}
                          />
                        </div>
                        <Button 
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeWallet(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-11 w-11 rounded-xl"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={addWallet}
                    className="w-full border-dashed border-white/20 hover:border-blue-500/50 hover:bg-blue-500/5 rounded-xl h-12"
                    data-testid="add-wallet-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Wallet Address
                  </Button>
                  <p className="text-xs text-slate-500 p-3 rounded-lg bg-white/5">
                    Crypto wallets will be screened for mixer usage, dark web associations, and sanctioned addresses.
                  </p>
                </div>
              )}

              {/* Step 4: Review */}
              {step === 4 && (
                <div className="space-y-5">
                  <div className="glass-card rounded-xl p-5">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Personal Information</h4>
                    <div className="grid grid-cols-2 gap-y-3 text-sm">
                      <span className="text-slate-500">Name</span>
                      <span className="text-white font-medium">{getValues("name")}</span>
                      <span className="text-slate-500">Email</span>
                      <span className="text-white font-medium">{getValues("email")}</span>
                      <span className="text-slate-500">Country</span>
                      <span className="text-white font-medium">{getValues("country")}</span>
                    </div>
                  </div>
                  {business.companyName && (
                    <div className="glass-card rounded-xl p-5">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Business Details</h4>
                      <div className="grid grid-cols-2 gap-y-3 text-sm">
                        <span className="text-slate-500">Company</span>
                        <span className="text-white font-medium">{business.companyName}</span>
                        <span className="text-slate-500">Reg #</span>
                        <span className="text-white font-medium font-mono-data">{business.registrationNumber || '-'}</span>
                      </div>
                    </div>
                  )}
                  {wallets.length > 0 && (
                    <div className="glass-card rounded-xl p-5">
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Crypto Wallets</h4>
                      {wallets.map((w, i) => (
                        <div key={i} className="text-sm text-white font-mono-data truncate">
                          <span className="text-slate-500">{w.network}:</span> {w.address}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t border-white/5">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setStep(Math.max(1, step - 1))}
                  disabled={step === 1}
                  className="border-white/10 hover:bg-white/5 rounded-xl px-6"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                {step < 4 ? (
                  <Button 
                    type="button"
                    onClick={handleNext}
                    className="btn-premium text-white rounded-xl px-6"
                    data-testid="next-step-btn"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    type="button"
                    onClick={onSubmit}
                    disabled={loading}
                    className="btn-premium text-white rounded-xl px-8"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Run Assessment
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
    </div>
  );
}
